// Height-based pagination for the Terms module, mirroring how Intro/Notes
// paginate in introRichText.js. Sections render as a 2-column card grid, so we
// pack whole ROWS (pairs of cards, row height = tallest card) against a page
// budget instead of a fixed section count — fixed counts left pages ~2/3 empty.
//
// Constants calibrated to styles/proposal.css:
//   fixed page content ≈ 964px (1056 page − frame header 52 − accent 4 − footer 36)
//   minus .proposal-section padding (80) and headline block (~75) → ~810 usable.
//   Kept under that so interpolated variables growing the text can't overflow.
const PAGE_BODY_HEIGHT = 780
const ROW_GAP = 16 // .proposal-terms__grid gap
const CARD_PADDING = 28 // .proposal-terms__card 14px top + bottom
const CARD_TITLE_HEIGHT = 20 // 12px bold title line + 4px margin
const CARD_LINE_HEIGHT = 17 // 11px body font × 1.5 line-height
const CARD_LINE_CHARS = 58 // ~320px of text width in one grid column
// Acceptance card (header + plan row + signing room + sig labels) + its 26px
// top margin + the "Questions? Contact…" how-to line underneath.
const ACCEPTANCE_HEIGHT = 310

// Content renders white-space: pre-line, so every \n is a hard break and
// blank lines between paragraphs cost a full line.
function estimateSectionHeight (section) {
  const lines = String(section.content || '')
    .split('\n')
    .reduce((sum, segment) => sum + Math.max(1, Math.ceil(segment.trim().length / CARD_LINE_CHARS)), 0)
  return CARD_PADDING + (section.title ? CARD_TITLE_HEIGHT : 0) + (lines * CARD_LINE_HEIGHT)
}

// Pair sections into grid rows in order; breaking only between rows keeps the
// side-by-side pairing identical no matter which page a row lands on.
function packRows (sections) {
  const rows = []
  for (let i = 0; i < sections.length; i += 2) {
    const pair = sections.slice(i, i + 2)
    rows.push({ sections: pair, height: Math.max(...pair.map(estimateSectionHeight)) })
  }
  return rows
}

export function paginateTermsPage (data = {}, hasAcceptance = false) {
  const fallbackHeadline = hasAcceptance ? 'Terms & Acceptance' : 'Terms & Conditions'
  const toPage = (sections, idx, lastIdx) => ({
    ...data,
    headline: idx === 0 ? data.headline : `${data.headline || fallbackHeadline} continued`,
    sections,
    hideAcceptance: !hasAcceptance || idx !== lastIdx,
  })

  const visibleSections = (data.sections || []).filter(section => !section.hidden)
  if (!visibleSections.length) {
    return [{ ...data, sections: [], hideAcceptance: !hasAcceptance }]
  }

  const pages = []
  let currentSections = []
  let usedHeight = 0

  packRows(visibleSections).forEach(row => {
    const rowHeight = row.height + (currentSections.length ? ROW_GAP : 0)
    if (currentSections.length && usedHeight + rowHeight > PAGE_BODY_HEIGHT) {
      pages.push(currentSections)
      currentSections = [...row.sections]
      usedHeight = row.height
    } else {
      currentSections.push(...row.sections)
      usedHeight += rowHeight
    }
  })

  if (hasAcceptance) {
    // The acceptance block lives on the final page; if it can't fit under the
    // remaining rows, give it a page of its own rather than overflowing.
    if (currentSections.length && usedHeight + ROW_GAP + ACCEPTANCE_HEIGHT > PAGE_BODY_HEIGHT) {
      pages.push(currentSections)
      currentSections = []
    }
    pages.push(currentSections)
  } else if (currentSections.length) {
    pages.push(currentSections)
  }

  return pages.map((sections, idx) => toPage(sections, idx, pages.length - 1))
}
